// src/components/pages/benefaction/AddDonorModal.tsx
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import ReCAPTCHA from "react-google-recaptcha";
import { useTranslation } from "../../../utils/i18nStub";
import axios from "axios";

import {
  createDonor,
  startDonorOtp,
  verifyDonorOtp,
  type CreateDonorRequest,
  type CreateDonorResponse,
} from "../../../services/donorService";
import { API_BASE_URL, RECAPTCHA_SITE_KEY } from "../../../config";
import { normalizeKsaPhone } from "../../../utils/phone";

/* ---------- helpers ---------- */
const RESEND_COOLDOWN_S = 60;

function toFullGregorian(s?: string | null): string | null {
  if (!s) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  if (/^\d{4}-\d{2}$/.test(s)) return `${s}-01`;
  return null;
}

function asErrMessage(e: unknown): string {
  if (axios.isAxiosError(e)) {
    const s = e.response?.status;
    const b = e.response?.data as any;
    const msg =
      (b && typeof b === "object" && (b.message || b.error || b.details || b.title)) ||
      (typeof b === "string" ? b.slice(0, 300) : "") ||
      e.message ||
      String(e);
    return s ? `${msg} (HTTP ${s})` : msg;
  }
  return String(e);
}

function friendlyArabic(msg: string): string | null {
  const m = (msg || "").toLowerCase();
  if (m.includes("not valid") && m.includes("nin")) return "رقم الهوية غير صحيح أو لا يطابق تاريخ الميلاد.";
  if (m.includes("operator-id")) return "رقم الهوية غير مقبول من مزود الخدمة.";
  if (m.includes("captcha")) return "يرجى اجتياز اختبار reCAPTCHA.";
  if (m.includes("401")) return "انتهت الجلسة. الرجاء تسجيل الدخول مرة أخرى.";
  if (m.includes("otp")) return "رمز التحقق غير صحيح أو منتهي.";
  return null;
}

/* ---------- hijri lists ---------- */
function useHijriLists() {
  const now = new Date().getFullYear();
  const approxHijri = now - 579;
  const years = useMemo(
    () => Array.from({ length: 150 }, (_, i) => String(approxHijri - i)),
    [approxHijri]
  );
  const months: Array<[string, string]> = useMemo(
    () => [
      ["01", "محرم"],
      ["02", "صفر"],
      ["03", "ربيع الأول"],
      ["04", "ربيع الثاني"],
      ["05", "جمادى الأولى"],
      ["06", "جمادى الآخرة"],
      ["07", "رجب"],
      ["08", "شعبان"],
      ["09", "رمضان"],
      ["10", "شوال"],
      ["11", "ذو القعدة"],
      ["12", "ذو الحجة"],
    ],
    []
  );
  return { years, months };
}

/* ---------- public Yaqeen verify ---------- */
async function verifyYaqeenPublic(identificationId: string, dateOfBirth: string) {
  const url = `${API_BASE_URL}/api/yaqeen/verify-public`;
  const res = await axios.post(
    url,
    { identificationId, dateOfBirth },
    {
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      validateStatus: (s) => s >= 200 && s < 300,
    }
  );

  const d = res.data ?? {};
  const s = d.snapshot ?? {};
  const v = d.verification ?? {};

  const nameAr =
    s.nameAR ||
    [
      v?.personBasicInfo?.firstName,
      v?.personBasicInfo?.fatherName,
      v?.personBasicInfo?.grandFatherName,
      v?.personBasicInfo?.familyName,
    ]
      .filter(Boolean)
      .join(" ")
      .trim();

  const dobHijri = s.dobHijri || v?.personBasicInfo?.convertDate?.dateString || null;
  const dobGregorian = s.dobGregorian || null;

  return { ok: true, nameAr: nameAr || "", dobHijri, dobGregorian };
}

/* ---------- component ---------- */
type Props = { open: boolean; onClose: () => void; onCreated: (d: CreateDonorResponse) => void };
type Step = 1 | 2;
type DonorType = "direct" | "indirect";

const AddDonorModal: React.FC<Props> = ({ open, onClose, onCreated }) => {
  const { t } = useTranslation(["BenefactorList"]);
  const dir = "rtl";
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  useEffect(() => {
    if (!open) return;
    const style = document.createElement("style");
    style.setAttribute("data-from", "AddDonorModal");
    style.innerHTML = `
      .grecaptcha-badge { z-index: 2147483647 !important; }
      iframe[title="recaptcha challenge"] { z-index: 2147483647 !important; }
    `;
    document.head.appendChild(style);
    return () => {
      try {
        document.head.removeChild(style);
      } catch {}
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [errAr, setErrAr] = useState("");
  const [step, setStep] = useState<Step>(1);

  const [donorType, setDonorType] = useState<DonorType>("indirect");

  // step 1
  const [nationalId, setNationalId] = useState("");
  const { years: hijriYears, months: hijriMonths } = useHijriLists();
  const [dobHijriYear, setDobHijriYear] = useState("");
  const [dobHijriMonth, setDobHijriMonth] = useState("");
  const [dobGregDate, setDobGregDate] = useState("");

  const [verifiedNameAr, setVerifiedNameAr] = useState("");
  const [dobHijriVerified, setDobHijriVerified] = useState<string | null>(null);
  const [dobGregVerified, setDobGregVerified] = useState<string | null>(null);

  const [captchaToken, setCaptchaToken] = useState("");

  // step 2
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");

  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (!open) return;
    setBusy(false);
    setErr("");
    setErrAr("");
    setStep(1);

    setDonorType("indirect");

    setNationalId("");
    setDobHijriYear("");
    setDobHijriMonth("");
    setDobGregDate("");
    setVerifiedNameAr("");
    setDobHijriVerified(null);
    setDobGregVerified(null);

    setPhone("");
    setEmail("");
    setOtpSent(false);
    setOtpVerified(false);
    setOtpCode("");
    setCooldown(0);
    setCaptchaToken("");
    recaptchaRef.current?.reset();
  }, [open]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = window.setInterval(() => setCooldown((s) => (s <= 1 ? 0 : s - 1)), 1000);
    return () => window.clearInterval(id);
  }, [cooldown]);

  const isCitizen = /^\s*1/.test(nationalId);
  const isResident = /^\s*[23]/.test(nationalId);

  const dobForVerify = useMemo(() => {
    if (isCitizen) return dobHijriYear && dobHijriMonth ? `${dobHijriYear}-${dobHijriMonth}` : "";
    if (isResident) return dobGregDate || "";
    return "";
  }, [isCitizen, isResident, dobHijriYear, dobHijriMonth, dobGregDate]);

  function showError(m: string) {
    setErr(m);
    setErrAr(friendlyArabic(m) || "");
  }

  function validateStep1(): string | null {
    if (!/^\d{10}$/.test(nationalId.replace(/\D+/g, "")))
      return t("nationalIdInvalid", "رقم الهوية الوطنية غير صحيح");
    if (!dobForVerify) return t("dobRequired", "تاريخ الميلاد مطلوب");
    return null;
  }

  
  async function onVerifyIdentity(e: React.FormEvent) {
    e.preventDefault();
    setErr("");
    setErrAr("");

    const v = validateStep1();
    if (v) return showError(v);

    setBusy(true);
    try {
      const r = await verifyYaqeenPublic(nationalId.trim(), dobForVerify);

      setVerifiedNameAr(r.nameAr || "");
      setDobHijriVerified(r.dobHijri || null);
      setDobGregVerified(r.dobGregorian || null);

      // Reset step-2 fields only
      setPhone("");
      setEmail("");
      setOtpSent(false);
      setOtpVerified(false);
      setOtpCode("");
      setCooldown(0);

      setStep(2);
    } catch (e2) {
      showError(asErrMessage(e2));
    } finally {
      setBusy(false);
    }
  }

async function onSendOtp() {
  setErr("");
  setErrAr("");

  const trimmedEmail = (email || "").trim().toLowerCase();

  if (!trimmedEmail) {
    return showError(t("emailRequired", "البريد الإلكتروني مطلوب"));
  }

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail);
  if (!emailValid) {
    return showError(t("emailInvalid", "البريد الإلكتروني غير صحيح"));
  }

  if (RECAPTCHA_SITE_KEY && !captchaToken) {
    return showError(t("captchaRequired", "يرجى اجتياز اختبار reCAPTCHA"));
  }

  if (cooldown > 0) return;

  setBusy(true);
  try {
    const res = await startDonorOtp("email", trimmedEmail, captchaToken);

    if (!res.ok) {
      return showError(res.message || t("otpSendFailed", "فشل إرسال رمز التحقق"));
    }

    setOtpSent(true);
    setCooldown(res.cooldownSeconds ?? RESEND_COOLDOWN_S);
  } catch (e2) {
    showError(asErrMessage(e2));
  } finally {
    setBusy(false);
  }
}

  async function onVerifyOtp() {
  setErr("");
  setErrAr("");

  const trimmedEmail = (email || "").trim().toLowerCase();

  if (!trimmedEmail) {
    return showError(t("emailRequired", "البريد الإلكتروني مطلوب"));
  }

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail);
  if (!emailValid) {
    return showError(t("emailInvalid", "البريد الإلكتروني غير صحيح"));
  }

  if (!otpCode || otpCode.trim().length < 4) {
    return showError(t("otpCodeRequired", "رمز التحقق مطلوب"));
  }

  setBusy(true);
  try {
    const res = await verifyDonorOtp("email", trimmedEmail, otpCode.trim());

    if (!res.ok) {
      return showError(res.message || t("otpInvalid", "رمز التحقق غير صحيح"));
    }

    setOtpVerified(true);
  } catch (e2) {
    showError(asErrMessage(e2));
  } finally {
    setBusy(false);
  }
}
  async function onSave(e: React.FormEvent) {
    e.preventDefault();
    setErr("");
    setErrAr("");

    if (!otpVerified) {
      return showError(t("mustVerifyIdentifier", "يجب التحقق من وسيلة التواصل أولاً"));
    }

    const normalizedPhone = phone?.trim()
      ? normalizeKsaPhone(phone).normalized ?? null
      : null;

    const trimmedEmail = email?.trim() ? email.trim().toLowerCase() : null;

    // at least one contact detail should exist
    if (!normalizedPhone && !trimmedEmail) {
      return showError(t("phoneOrEmailRequired", "يجب إدخال رقم هاتف أو بريد إلكتروني واحد على الأقل"));
    }

    const dobForApi = isCitizen ? toFullGregorian(dobGregVerified) : toFullGregorian(dobGregDate);

    setBusy(true);
    try {
      const payload: CreateDonorRequest = {
        name: verifiedNameAr || "",
        nationalId: nationalId.trim(),
        DonorType: donorType,
        phone: normalizedPhone,
        email: trimmedEmail,
        dob: dobForApi,
        creation_platform: "donation_web_portal",
      };

      const created = await createDonor(payload);
      onCreated(created);
      onClose();
    } catch (e2) {
      showError(asErrMessage(e2));
    } finally {
      setBusy(false);
    }
  }

  if (!open) return null;

  const overlayStyle: React.CSSProperties = { position: "fixed", inset: 0, zIndex: 1000 };
  const backdropStyle: React.CSSProperties = {
    position: "absolute",
    inset: 0,
    background: "rgba(17, 24, 39, 0.55)",
    backdropFilter: "blur(2px)",
  };
  const wrapperStyle: React.CSSProperties = {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
  };

  return createPortal(
    <div aria-modal="true" role="dialog" style={overlayStyle}>
      <style>{`
        :root{
          --dga-bg:#ffffff;
          --dga-border:rgba(16,24,40,0.12);
          --dga-muted:#6b7280;
          --dga-text:#111827;
          --dga-card:#ffffff;
          --dga-soft:#f8fafc;
          --dga-primary:#0f766e;
          --dga-primary-2:#0d9488;
          --dga-ring: rgba(15,118,110,0.28);
          --dga-danger:#b42318;
          --dga-danger-bg:#fef3f2;
          --radius:18px;
        }

        .dga-modal{
          width: min(820px, 95vw);
          max-height: 90vh;
          background: var(--dga-bg);
          border: 1px solid var(--dga-border);
          border-radius: 22px;
          box-shadow: 0 24px 60px rgba(0,0,0,0.20);
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }

        .dga-header{
          display:flex;
          align-items:center;
          justify-content:space-between;
          gap:12px;
          padding:16px 18px;
          border-bottom: 1px solid var(--dga-border);
          background: var(--dga-soft);
        }

        .dga-title{
          margin:0;
          font-size: 18px;
          font-weight: 800;
          color: var(--dga-text);
          letter-spacing: -0.2px;
        }
        .dga-subtitle{
          margin:4px 0 0 0;
          font-size: 12px;
          color: var(--dga-muted);
        }

        .dga-iconBubble{
          height:40px;width:40px;
          border-radius: 16px;
          display:grid;
          place-items:center;
          background: rgba(15,118,110,0.10);
          color: var(--dga-primary);
          flex: 0 0 auto;
        }

        .dga-closeBtn{
          height:40px;width:40px;
          border-radius: 14px;
          border: 1px solid var(--dga-border);
          background: #fff;
          display:inline-flex;
          align-items:center;
          justify-content:center;
          cursor:pointer;
          transition: background .15s ease, transform .05s ease;
        }
        .dga-closeBtn:hover{ background:#f3f4f6; }
        .dga-closeBtn:active{ transform: translateY(1px); }
        .dga-closeBtn:focus{ outline:none; box-shadow: 0 0 0 4px var(--dga-ring); border-color: rgba(15,118,110,0.55); }

        .dga-body{
          padding: 16px 18px;
          overflow: auto;
          -webkit-overflow-scrolling: touch;
          flex: 1 1 auto;
          background: var(--dga-bg);
        }

        .dga-card{
          border: 1px solid var(--dga-border);
          border-radius: var(--radius);
          background: var(--dga-card);
          padding: 14px;
        }

        .dga-sectionTitle{
          font-size: 13px;
          font-weight: 800;
          color: var(--dga-text);
          margin:0;
        }
        .dga-sectionHint{
          font-size: 12px;
          color: var(--dga-muted);
          margin:6px 0 0 0;
        }

        .dga-grid{
          display:grid;
          grid-template-columns: 1fr;
          gap: 12px;
          margin-top: 12px;
        }
        @media (min-width: 640px){
          .dga-grid.two{ grid-template-columns: 1fr 1fr; }
          .dga-span2{ grid-column: 1 / -1; }
        }

        .dga-label{
          display:block;
          font-size: 13px;
          font-weight: 700;
          color: #374151;
          margin-bottom: 6px;
        }

        .dga-input,
        .dga-select{
          width: 100%;
          height: 48px;
          border-radius: var(--radius);
          border: 1px solid rgba(16,24,40,0.16);
          background: #fff;
          padding: 0 14px;
          font-size: 14px;
          color: var(--dga-text);
          box-shadow: 0 1px 0 rgba(16,24,40,0.04), 0 1px 2px rgba(16,24,40,0.06);
          outline: none;
          transition: box-shadow .15s ease, border-color .15s ease, background .15s ease;
          direction: rtl;
        }
        .dga-input::placeholder{ color: #9ca3af; }

        .dga-input:focus,
        .dga-select:focus{
          border-color: rgba(15,118,110,0.70);
          box-shadow: 0 0 0 4px var(--dga-ring), 0 1px 2px rgba(16,24,40,0.06);
          outline: none !important;
        }

        .dga-input[readonly]{
          background:#f9fafb;
        }

        .dga-select{
          padding-left: 42px;
          appearance: none;
          -webkit-appearance: none;
          -moz-appearance: none;
          background-image: none;
        }

        .dga-selectWrap{ position: relative; }
        .dga-caret{
          position:absolute;
          left: 14px;
          top: 50%;
          transform: translateY(-50%);
          pointer-events:none;
          color:#9ca3af;
        }

        .dga-row{
          display:flex;
          gap:10px;
          align-items:center;
          flex-wrap: wrap;
        }
        .dga-grow{ flex: 1 1 260px; min-width: 220px; }
        .dga-fixed{ flex: 0 0 auto; }

        .dga-btn{
          height: 44px;
          padding: 0 16px;
          border-radius: 16px;
          border: 1px solid rgba(16,24,40,0.16);
          background: #fff;
          color: #374151;
          font-weight: 800;
          font-size: 13px;
          cursor:pointer;
          transition: background .15s ease, transform .05s ease, box-shadow .15s ease, border-color .15s ease;
          white-space: nowrap;
        }
        .dga-btn:hover{ background:#f3f4f6; }
        .dga-btn:active{ transform: translateY(1px); }
        .dga-btn:focus{ outline:none; box-shadow: 0 0 0 4px var(--dga-ring); border-color: rgba(15,118,110,0.55); }

        .dga-btnPrimary{
          border-color: transparent;
          background: var(--dga-primary);
          color: #fff;
        }
        .dga-btnPrimary:hover{ background: var(--dga-primary-2); }

        .dga-btnOutlinePrimary{
          border-color: rgba(15,118,110,0.70);
          color: var(--dga-primary);
          background:#fff;
        }
        .dga-btnOutlinePrimary:hover{ background: rgba(15,118,110,0.06); }

        .dga-btn[disabled]{
          opacity: .55;
          cursor: not-allowed;
        }

        .dga-alertErr{
          border: 1px solid rgba(180,35,24,0.25);
          background: var(--dga-danger-bg);
          color: var(--dga-danger);
          border-radius: var(--radius);
          padding: 12px 14px;
          margin-bottom: 12px;
        }
        .dga-alertOk{
          border: 1px solid rgba(16,185,129,0.25);
          background: rgba(16,185,129,0.10);
          color: #065f46;
          border-radius: var(--radius);
          padding: 12px 14px;
          margin-bottom: 12px;
        }

        .dga-footer{
          position: sticky;
          bottom: 0;
          z-index: 10;
          padding: 12px 18px 14px;
          background: rgba(255,255,255,0.96);
          backdrop-filter: blur(6px);
          border-top: 1px solid var(--dga-border);
        }
        .dga-actions{
          display:flex;
          gap:10px;
          flex-wrap: wrap;
          justify-content: flex-start;
        }

        .dga-toggle{
          display:flex;
          gap:8px;
          margin-top: 12px;
          padding: 6px;
          border: 1px solid rgba(16,24,40,0.12);
          background: #fff;
          border-radius: 16px;
        }
        .dga-toggleBtn{
          flex: 1 1 0;
          height: 40px;
          border-radius: 14px;
          border: 1px solid rgba(16,24,40,0.12);
          background: #fff;
          color: #374151;
          font-weight: 800;
          font-size: 13px;
          cursor: pointer;
          transition: background .15s ease, border-color .15s ease, box-shadow .15s ease;
        }
        .dga-toggleBtn:hover{ background:#f3f4f6; }
        .dga-toggleBtnActive{
          border-color: rgba(15,118,110,0.70);
          background: rgba(15,118,110,0.10);
          color: var(--dga-primary);
        }
      `}</style>

      <div style={backdropStyle} onClick={onClose} />

      <div style={wrapperStyle}>
        <div dir={dir} className="dga-modal">
          <div className="dga-header">
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div className="dga-iconBubble" aria-hidden="true">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </div>
              <div>
                <h3 className="dga-title">{t("addDonor", "إضافة مُوكِّل")}</h3>
                <p className="dga-subtitle">
                  {step === 1
                    ? t("step1Title", "التحقق من الهوية")
                    : t("step2Title", "تأكيد المعرّف وإكمال البيانات")}
                </p>
              </div>
            </div>

            <button type="button" onClick={onClose} className="dga-closeBtn" aria-label={t("close", "إغلاق")}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
          </div>

          <div className="dga-body">
            {(err || errAr) && (
              <div className="dga-alertErr">
                {errAr ? <div style={{ fontWeight: 800, marginBottom: 6 }}>{errAr}</div> : null}
                <div style={{ fontSize: 12, opacity: 0.9 }}>{err}</div>
              </div>
            )}

            {step === 1 && (
              <form onSubmit={onVerifyIdentity}>
                <div className="dga-card">
                  <div>
                    <p className="dga-sectionTitle">{t("identitySection", "بيانات التحقق")}</p>
                    <p className="dga-sectionHint">{t("identityHint", "أدخل الهوية وتاريخ الميلاد ثم أكمل التحقق")}</p>
                  </div>

                  <div style={{ marginTop: 12 }}>
                    <label className="dga-label">{t("donorType", "نوع المُوكِّل")}</label>
                    <div className="dga-toggle" role="group" aria-label={t("donorType", "نوع المُوكِّل")}>
                      <button
                        type="button"
                        className={`dga-toggleBtn ${donorType === "direct" ? "dga-toggleBtnActive" : ""}`}
                        onClick={() => setDonorType("direct")}
                        disabled={busy}
                      >
                        {t("direct", "مباشر")}
                      </button>
                      <button
                        type="button"
                        className={`dga-toggleBtn ${donorType === "indirect" ? "dga-toggleBtnActive" : ""}`}
                        onClick={() => setDonorType("indirect")}
                        disabled={busy}
                      >
                        {t("indirect", "غير مباشر")}
                      </button>
                    </div>
                  </div>

                  <div className="dga-grid two">
                    <div className="dga-span2">
                      <label className="dga-label">{t("nationalId", "الهوية الوطنية")}</label>
                      <input
                        className="dga-input"
                        value={nationalId}
                        onChange={(e) => setNationalId(e.target.value.replace(/\D+/g, ""))}
                        inputMode="numeric"
                        maxLength={10}
                        autoComplete="off"
                      />
                    </div>

                    {isCitizen ? (
                      <>
                        <div>
                          <label className="dga-label">{t("hijriYear", "السنة الهجرية")}</label>
                          <div className="dga-selectWrap">
                            <select className="dga-select" value={dobHijriYear} onChange={(e) => setDobHijriYear(e.target.value)}>
                              <option value="">{t("choose", "اختر")}</option>
                              {hijriYears.map((y) => (
                                <option key={y} value={y}>{y}</option>
                              ))}
                            </select>
                            <span className="dga-caret" aria-hidden="true">
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                              </svg>
                            </span>
                          </div>
                        </div>

                        <div>
                          <label className="dga-label">{t("hijriMonth", "الشهر الهجري")}</label>
                          <div className="dga-selectWrap">
                            <select className="dga-select" value={dobHijriMonth} onChange={(e) => setDobHijriMonth(e.target.value)}>
                              <option value="">{t("choose", "اختر")}</option>
                              {hijriMonths.map(([val, name]) => (
                                <option key={val} value={val}>{`${val} - ${name}`}</option>
                              ))}
                            </select>
                            <span className="dga-caret" aria-hidden="true">
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                              </svg>
                            </span>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="dga-span2">
                        <label className="dga-label">{t("gregorianDate", "التاريخ الميلادي")}</label>
                        <input
                          type="date"
                          className="dga-input"
                          value={dobGregDate}
                          onChange={(e) => setDobGregDate(e.target.value)}
                        />
                      </div>
                    )}

                    <div className="dga-span2">
                      <div className="dga-card" style={{ background: "#f8fafc" }}>
                        {RECAPTCHA_SITE_KEY ? (
                          <ReCAPTCHA
                            ref={recaptchaRef}
                            sitekey={RECAPTCHA_SITE_KEY}
                            onChange={(tok) => setCaptchaToken(tok || "")}
                            hl="ar"
                          />
                        ) : (
                          <div style={{ fontSize: 13, color: "#92400e" }}>reCAPTCHA not configured.</div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="dga-footer">
                  <div className="dga-actions">
                    <button type="button" onClick={onClose} className="dga-btn">
                      {t("cancel", "إلغاء")}
                    </button>
                    <button type="submit" disabled={busy} className="dga-btn dga-btnPrimary">
                      {busy ? t("saving", "جاري الحفظ...") : t("verify", "التحقق")}
                    </button>
                  </div>
                </div>
              </form>
            )}

            {step === 2 && (
              <form onSubmit={onSave}>
                {(verifiedNameAr || dobHijriVerified || dobGregVerified) && (
                  <div className="dga-alertOk">
                    {verifiedNameAr && (
                      <div>
                        {t("verifiedAsAr", "تم التحقق كـ")} <strong>{verifiedNameAr}</strong>
                      </div>
                    )}
                    {dobHijriVerified && (
                      <div>
                        {t("dobHijri", "تاريخ الميلاد الهجري")}: <strong>{dobHijriVerified}</strong>
                      </div>
                    )}
                    {dobGregVerified && (
                      <div>
                        {t("dobGregorian", "تاريخ الميلاد الميلادي")}: <strong>{dobGregVerified}</strong>
                      </div>
                    )}
                  </div>
                )}

                <div className="dga-card">
                  <div>
                    <p className="dga-sectionTitle">{t("contactSection", "بيانات التحقق والتواصل")}</p>
                    <p className="dga-sectionHint">{t("contactHint", "اختر نوع المعرّف، تحقق عبر OTP، ثم أكمل البيانات")}</p>
                  </div>

                  <div className="dga-grid two">
                                        
                    <div>
                      <label className="dga-label">{t("phone", "الهاتف")}</label>
                      <input
                        className="dga-input"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder="05XXXXXXXX أو 9665XXXXXXXX"
                      />
                    </div>

                    <div>
                      <label className="dga-label">{t("email", "البريد الإلكتروني")}</label>
                      <input
                        className="dga-input"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="name@example.com"
                      />
                    </div>

                    <div className="dga-span2">
                      <div className="dga-row">
                        <div className="dga-grow">
                          <input
                            className="dga-input"
                            placeholder={t("otpCodePlaceholder", "رمز التحقق")}
                            value={otpCode}
                            onChange={(e) => setOtpCode(e.target.value)}
                            inputMode="numeric"
                            pattern="\\d*"
                            readOnly={!otpSent || otpVerified}
                          />
                        </div>

                        <div className="dga-fixed">
                          <button
                            type="button"
                            onClick={onSendOtp}
                            disabled={busy || cooldown > 0 || otpVerified}
                            className="dga-btn dga-btnOutlinePrimary"
                          >
                            {cooldown > 0
                              ? t("resendIn", `إعادة الإرسال خلال {{s}} ثانية`, { s: cooldown })
                              : otpSent
                              ? t("resend", "إعادة الإرسال")
                              : t("sendOtp", "إرسال رمز التحقق")}
                          </button>
                        </div>

                        <div className="dga-fixed">
                          <button
                            type="button"
                            onClick={onVerifyOtp}
                            disabled={busy || !otpSent || otpVerified}
                            className="dga-btn dga-btnPrimary"
                            style={otpVerified ? { background: "#16a34a" } : undefined}
                          >
                            {otpVerified ? t("otpVerified", "تم التحقق") : t("verifyCode", "التحقق من الرمز")}
                          </button>
                        </div>
                      </div>

                      <div style={{ marginTop: 8, fontSize: 12, color: "#6b7280" }}>
                        {t("otpHintGeneric", "لن تتمكن من الحفظ قبل التحقق من المعرّف المختار.")}
                      </div>
                    </div>



                  </div>
                </div>

                <div className="dga-footer">
                  <div className="dga-actions">
                    <button type="button" onClick={() => setStep(1)} className="dga-btn">
                      {t("back", "رجوع")}
                    </button>

                    <button type="submit" disabled={busy || !otpVerified} className="dga-btn dga-btnPrimary">
                      {busy ? t("saving", "جاري الحفظ...") : t("save", "حفظ")}
                    </button>
                  </div>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AddDonorModal;